import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useLoginFlow } from '../../hooks/useLoginFlow';
import { useAuth } from '../../hooks/useAuth';
import { NOMBRE_PRODUCTO, destinoPostLogin } from '../../config/producto';
import { EmailStep } from './steps/EmailStep';
import { OTPStep } from './steps/OTPStep';
import { SuccessStep } from './steps/SuccessStep';
import loginBgMesh from '../../assets/login-bg-mesh.png';
import logoPrimary from '../../assets/logo-primary.png';

export const AccessPage: React.FC = () => {
    const {
        step,
        email,
        otp,
        timer,
        devOtp,
        isSubmitting,
        isResending,
        error,
        handleEmailChange,
        handleEmailSubmit,
        handleOtpChange,
        handleOtpSubmit,
        handleOtpComplete,
        handleResend,
        handleBack
    } = useLoginFlow();
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation();

    // Con sesión, /login no muestra el formulario: lleva adonde iba el usuario. Es el MISMO destino
    // que usa el login (destinoPostLogin), así el render que sigue a login() no pisa el deep-link.
    // En 'success' no se redirige acá: el flujo termina la animación y navega él.
    if (!isLoading && isAuthenticated && step !== 'success') {
        return <Navigate to={destinoPostLogin(location.state)} replace />;
    }

    return (
        <div className="min-h-screen w-full bg-[#F3F4F6] flex items-center justify-center p-4 lg:p-6 font-sans text-[#111827]">

            {/* Main Card Container with Outer Glow */}
            <div className="bg-white w-full max-w-[1000px] min-h-[600px] rounded-[32px] shadow-[0_20px_60px_-15px_rgba(124,58,237,0.25)] p-5 flex overflow-hidden relative animate-fade-in border border-white/50 ring-1 ring-indigo-500/10">

                {/* Left Column: Visual Branding - Desktop Only */}
                {/* Added internal shadow/glow to banner for 'futuristic light' effect */}
                <div className="hidden lg:flex w-1/2 flex-col justify-between relative rounded-[24px] overflow-hidden p-8 xl:p-12 text-white shadow-[0_0_40px_-10px_rgba(124,58,237,0.4)] ring-1 ring-white/20">
                    {/* Background Image - Mesh Gradient */}
                    <div
                        className="absolute inset-0 bg-cover bg-center z-0"
                        style={{ backgroundImage: `url(${loginBgMesh})` }}
                    />

                    {/* Logo Area - Top Left */}
                    <div className="relative z-10 pt-2">
                        {/* Primary Logo replaces Asterisk */}
                        <img src={logoPrimary} alt={NOMBRE_PRODUCTO} className="w-16 h-16 object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]" />
                    </div>

                    {/* Marketing Copy - Bottom Left */}
                    <div className="relative z-10 mb-4 space-y-3">
                        <p className="text-[15px] font-medium text-white/90 tracking-wide uppercase">
                            Agentes de marketing
                        </p>
                        <h2 className="text-3xl xl:text-[34px] font-bold leading-[1.2] text-white tracking-tight drop-shadow-md">
                            Entrá y automatizá el marketing de tu marca
                        </h2>
                    </div>
                </div>

                {/* Right Column: Form */}
                <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-20 py-8 relative bg-white">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex items-center gap-2 mb-8 absolute top-8 left-8">
                        <img src={logoPrimary} alt={NOMBRE_PRODUCTO} className="w-8 h-8 object-contain" />
                        <span className="font-bold text-text-primary">{NOMBRE_PRODUCTO}</span>
                    </div>

                    <div className="w-full max-w-[400px] mx-auto">
                        {step === 'email' && (
                            <EmailStep
                                email={email}
                                onChange={handleEmailChange}
                                onSubmit={handleEmailSubmit}
                                isLoading={isSubmitting}
                                error={error}
                            />
                        )}

                        {step === 'otp' && (
                            <OTPStep
                                email={email}
                                otp={otp}
                                timer={timer}
                                devOtp={devOtp}
                                onChange={handleOtpChange}
                                onComplete={handleOtpComplete}
                                onSubmit={handleOtpSubmit}
                                onResend={handleResend}
                                onBack={handleBack}
                                isLoading={isSubmitting}
                                isResending={isResending}
                                error={error}
                            />
                        )}

                        {step === 'success' && (
                            <SuccessStep />
                        )}

                        <div className="mt-12 text-center">
                            {/* Decía «Deenex AI», la plataforma anterior: el producto es DEENEX Activa (NOMBRE_PRODUCTO). */}
                            <p className="text-xs text-[#9CA3AF]">
                                {NOMBRE_PRODUCTO}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
